import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface EvidenceDetail {
  id: number
  evidence_type_id: number
  evidence_type_name: string | null
  evidence_type_desc: string | null
  evidence_date: string | null
  quantity: number | null
  filename: string | null
  notes: string | null
  user_name: string | null
  date_added: string | null
  added_by_name: string | null
  received_at: string | null
  received_by_name: string | null
  received_quantity: number | null
  rated_at: string | null
  rated_by_name: string | null
  rating: number | null
  rating_comments: string | null
  used: number | null
  status: number
}

interface Props {
  case_record: CaseRecord
  evidence: EvidenceDetail
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return date.toLocaleString()
}

function formatQuantity(qty: number | null, desc: string | null): string {
  if (qty === null || qty === undefined) return "-"
  if (desc === "mb") {
    if (qty > 1000) return `${(qty / 1000).toFixed(2)} gb`
    return `${qty.toFixed(2)} mb`
  }
  return `Qty: ${qty}`
}

function ratingStars(rating: number | null): string {
  if (rating === null || rating === undefined) return "Not rated"
  return "\u2605".repeat(rating) + "\u2606".repeat(5 - rating)
}

const STATUS_LABELS: Record<number, string> = {
  0: "Active",
  20: "Received",
}

const STATUS_STYLES: Record<number, string> = {
  0: "bg-hpi-pale-gray text-hpi-dark-gray",
  20: "bg-hpi-dark-green/20 text-hpi-dark-green",
}

export default function Show({ case_record, evidence: ev }: Props) {
  const [showRateForm, setShowRateForm] = useState(false)
  const [rating, setRating] = useState(5)
  const [ratingComments, setRatingComments] = useState("")
  const [receivedQty, setReceivedQty] = useState(ev.quantity?.toString() ?? "")

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Evidence", href: `/cases/${case_record.id}/evidence` },
    {
      title: ev.filename ?? `Evidence #${ev.id}`,
      href: `/cases/${case_record.id}/evidence/${ev.id}`,
    },
  ]

  const basePath = `/cases/${case_record.id}/evidence`

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this evidence?")) {
      router.delete(`${basePath}/${ev.id}`)
    }
  }

  const handleReceive = () => {
    if (confirm("Mark this evidence as received?")) {
      router.patch(`${basePath}/${ev.id}/receive`, {
        received_quantity: receivedQty,
      })
    }
  }

  const handleRate = () => {
    router.patch(`${basePath}/${ev.id}/rate`, {
      rating: rating,
      rating_comments: ratingComments,
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Evidence - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">
              {ev.evidence_type_name} Evidence
            </h1>
            <span
              className={`rounded px-2 py-1 text-sm ${STATUS_STYLES[ev.status] ?? "bg-hpi-pale-gray text-hpi-dark-gray"}`}
            >
              {STATUS_LABELS[ev.status] ?? "Unknown"}
            </span>
          </div>
          <div className="flex gap-2">
            <Link
              href={`${basePath}/${ev.id}/edit`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Edit
            </Link>
            {ev.status === 0 && (
              <button
                onClick={handleReceive}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white"
              >
                Receive
              </button>
            )}
            {ev.status === 20 && !ev.rated_at && (
              <button
                onClick={() => setShowRateForm(!showRateForm)}
                className="bg-hpi-yellow text-hpi-dark-gray hover:bg-hpi-yellow/80 rounded px-4 py-2"
              >
                Rate
              </button>
            )}
            <button
              onClick={handleDelete}
              className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
            >
              Delete
            </button>
          </div>
        </div>

        {ev.status === 0 && (
          <div className="border-hpi-dark-green/30 bg-hpi-dark-green/5 mb-4 rounded border p-4">
            <h3 className="mb-2 font-semibold">Receive Evidence</h3>
            <div className="flex items-end gap-4">
              <div>
                <label
                  htmlFor="received_qty"
                  className="mb-1 block text-sm font-medium"
                >
                  Received Quantity
                </label>
                <input
                  type="number"
                  id="received_qty"
                  step="0.01"
                  value={receivedQty}
                  onChange={(e) => setReceivedQty(e.target.value)}
                  className="border-hpi-light-gray rounded border px-3 py-2"
                />
              </div>
              <button
                onClick={handleReceive}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white"
              >
                Confirm Receipt
              </button>
            </div>
          </div>
        )}

        {showRateForm && (
          <div className="border-hpi-yellow/50 bg-hpi-yellow/10 mb-4 rounded border p-4">
            <h3 className="mb-2 font-semibold">Rate Evidence Quality</h3>
            <div className="mb-3">
              <label
                htmlFor="rating"
                className="mb-1 block text-sm font-medium"
              >
                Rating (0-5)
              </label>
              <select
                id="rating"
                value={rating}
                onChange={(e) => setRating(parseInt(e.target.value))}
                className="border-hpi-light-gray rounded border px-3 py-2"
              >
                {[5, 4, 3, 2, 1, 0].map((r) => (
                  <option key={r} value={r}>
                    {r} - {"\u2605".repeat(r)}
                    {"\u2606".repeat(5 - r)}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label
                htmlFor="rating_comments"
                className="mb-1 block text-sm font-medium"
              >
                Comments
              </label>
              <textarea
                id="rating_comments"
                value={ratingComments}
                onChange={(e) => setRatingComments(e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleRate}
                className="bg-hpi-yellow text-hpi-dark-gray hover:bg-hpi-yellow/80 rounded px-4 py-2"
              >
                Submit Rating
              </button>
              <button
                onClick={() => setShowRateForm(false)}
                className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-3">
          <div className="md:col-span-2">
            {ev.notes && (
              <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-2 font-semibold">Notes</h2>
                <div className="whitespace-pre-wrap">{ev.notes}</div>
              </div>
            )}

            {ev.rating_comments && (
              <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-2 font-semibold">
                  Rating Comments
                </h2>
                <div className="whitespace-pre-wrap">{ev.rating_comments}</div>
              </div>
            )}
          </div>

          <div>
            <div className="border-hpi-light-gray rounded border bg-white p-4">
              <h2 className="text-hpi-dark-gray mb-3 font-semibold">Details</h2>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-hpi-med-gray">Type</dt>
                  <dd>{ev.evidence_type_name}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Date</dt>
                  <dd>{formatDate(ev.evidence_date)}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Quantity</dt>
                  <dd>{formatQuantity(ev.quantity, ev.evidence_type_desc)}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Filename</dt>
                  <dd>{ev.filename ?? "-"}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Investigator</dt>
                  <dd>{ev.user_name ?? "-"}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Added</dt>
                  <dd>
                    {formatDateTime(ev.date_added)}
                    {ev.added_by_name && ` by ${ev.added_by_name}`}
                  </dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Rating</dt>
                  <dd>{ratingStars(ev.rating)}</dd>
                </div>
                {ev.received_at && (
                  <div>
                    <dt className="text-hpi-med-gray">Received</dt>
                    <dd>
                      {formatDateTime(ev.received_at)} by {ev.received_by_name}
                    </dd>
                  </div>
                )}
                {ev.received_quantity !== null &&
                  ev.received_quantity !== undefined && (
                    <div>
                      <dt className="text-hpi-med-gray">Received Qty</dt>
                      <dd>
                        {formatQuantity(
                          ev.received_quantity,
                          ev.evidence_type_desc,
                        )}
                      </dd>
                    </div>
                  )}
                {ev.rated_at && (
                  <div>
                    <dt className="text-hpi-med-gray">Rated</dt>
                    <dd>
                      {formatDateTime(ev.rated_at)} by {ev.rated_by_name}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}/evidence`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Evidence
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
